import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { useSelector } from "react-redux";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text } from "../elements";
import { formatAmountSuffix, getAcronym } from "../../utils/formatters";

const GRADIENTS = [
  { id: "projBlue", color: "#2351af" },
  { id: "projRed", color: "#c71414" },
  { id: "projGreen", color: "#18633a" },
];

const ProjectTooltip = ({ active, payload, label, colors }) => {
  if (!active || !payload?.length) return null;
  const fullName = payload[0].payload.fullName || label;
  return (
    <Box
      sx={{
        background: colors.bg,
        borderRadius: "8px",
        padding: "11px 18px 12px",
        border: `1px solid ${colors.frame}`,
        boxShadow: "0px 12px 25px 0px rgb(0 0 0 / 12%)",
        maxWidth: 220,
      }}
    >
      <Heading
        as="h6"
        style={{
          color: colors.title,
          marginBottom: "8px",
          fontSize: "12px",
          fontWeight: "600",
          textTransform: "uppercase",
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        {fullName}
      </Heading>
      {payload.map((entry, i) => (
        <Box key={i} display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box sx={{ width: 10, height: 10, backgroundColor: entry.color, borderRadius: "2px" }} />
          <Text
            as="span"
            style={{
              color: colors.text,
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            {entry.name}: {formatAmountSuffix(entry.value, true)}
          </Text>
        </Box>
      ))}
    </Box>
  );
};

export function ProjectSummaryChart({ fromDate, toDate }) {
  const { theme } = useContext(ThemeContext);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const userDetails = useSelector((state) => state.user?.user);
  const companyId = userDetails?.company?.id;

  const dark = { border: "#39496b", frame: "#2f3f61", title: "#f0f0f0", text: "#d1d1d1", bg: "#1b2b4d" };
  const light = { border: "#d1d1d1", frame: "#f0f0f0", title: "#403e57", text: "#5e5d72", bg: "#ffffff" };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await AnalyticsService.getCompanyProjectSummary({
        page: 1,
        limit: 11,
        ...(companyId ? { companyId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      });
      const rows = data?.rows || [];
      setChartData(
        rows.map((project) => ({
          name: getAcronym(project.projectName),
          fullName: project.projectName,
          Income: parseFloat(project.totalIncome || 0),
          Expense: parseFloat(project.totalExpense || 0),
          Profit: parseFloat(project.profit || 0),
        })),
      );
    } catch (error) {
      console.error("🚀 ~ ProjectSummaryChart ~ fetchData ~ error:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box className="mc-card" sx={{ mt: 0 }}>
      <Box className="mc-card-header" style={{ display: "flex", justifyContent: "center" }}>
        <Heading as="h2" className="mc-card-title" style={{ fontSize: "20px", fontWeight: 600, paddingBottom: "10px" }}>
          Project Summary
        </Heading>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      ) : chartData.length > 0 ? (
        <Box sx={{ height: 410 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <defs>
                {GRADIENTS.map((g) => (
                  <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={g.color} stopOpacity={0.9} />
                    <stop offset="100%" stopColor={g.color} stopOpacity={0.4} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                stroke={theme !== "light_mode" ? dark.border : light.border}
                strokeDasharray="1 3"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                stroke={theme !== "light_mode" ? dark.text : light.text}
                fontSize="11px"
                fontWeight="500"
                tickMargin={10}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                stroke={theme !== "light_mode" ? dark.text : light.text}
                fontSize="11px"
                fontWeight="500"
                tickFormatter={(v) => formatAmountSuffix(v)}
                width={80}
              />
              <RechartsTooltip
                content={<ProjectTooltip colors={theme !== "light_mode" ? dark : light} />}
                cursor={{ fill: theme !== "light_mode" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}
              />
              <Legend iconType="circle" wrapperStyle={{ paddingTop: "16px" }} />
              <Bar dataKey="Income" fill="url(#projBlue)" radius={[4, 4, 0, 0]} barSize={22} />
              <Bar dataKey="Expense" fill="url(#projRed)" radius={[4, 4, 0, 0]} barSize={22} />
              <Bar dataKey="Profit" fill="url(#projGreen)" radius={[4, 4, 0, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Text>No project data available</Text>
        </Box>
      )}
    </Box>
  );
}
